<template>
    <div class="products-grid">
        <a
            v-for="product in products"
            :key="product.id"
            :href="$router.resolve({ name: 'Product', query: { id: product.id } }).href"
            target="_blank"
            rel="noopener"
            class="product-card"
            ref="animationProducts"
            :aria-label="`View ${product.name}`"
        >
            <div class="product-image-wrap">
                <div
                    class="product-image"
                    :style="{ backgroundImage: `url(${product.image})` }"
                ></div>
            </div>

            <div class="product-info">
                <h3 class="product-name">{{ product.name }}</h3>
                <div class="product-price">
                    <span class="price">€ {{ product.price }}</span>
                    <span class="add-to-cart" aria-hidden="true">
                        <i class="fas fa-shopping-bag"></i>
                    </span>
                </div>
            </div>
        </a>
    </div>
</template>

<script>
    import { gsap } from 'gsap'
    import { ScrollTrigger } from 'gsap/ScrollTrigger'

    gsap.registerPlugin(ScrollTrigger)

    export default {
        name: 'ProductGrid',
        props: {
            products: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                productTweens: [],
                productTriggers: [],
                animationFrame: null,
                animationQueued: false
            }
        },
        watch: {
            products() {
                this.queueProductAnimation();
            },
            'products.length'() {
                this.queueProductAnimation();
            }
        },
        mounted() {
            this.queueProductAnimation();
        },
        methods: {
            queueProductAnimation() {
                if (this.animationQueued) {
                    return;
                }

                this.animationQueued = true;
                this.$nextTick(() => {
                    if (this.animationFrame) {
                        cancelAnimationFrame(this.animationFrame);
                    }

                    this.animationFrame = requestAnimationFrame(() => {
                        this.animationFrame = null;
                        this.animationQueued = false;
                        this.animateProducts();
                    });
                });
            },
            clearProductAnimation() {
                this.productTriggers.forEach((trigger) => trigger.kill());
                this.productTweens.forEach((tween) => tween.kill());
                this.productTriggers = [];
                this.productTweens = [];
            },
            animateProducts() {
                const productCards = gsap.utils.toArray(this.$refs.animationProducts);

                this.clearProductAnimation();

                if (productCards.length === 0) {
                    return;
                }

                if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                    gsap.set(productCards, { opacity: 1, x: 0, y: 0, scale: 1, clearProps: 'transform,opacity,willChange' });
                    return;
                }

                gsap.set(productCards, {
                    opacity: 0,
                    y: 22,
                    scale: 0.985,
                    willChange: 'transform, opacity'
                });

                const batchTriggers = ScrollTrigger.batch(productCards, {
                    start: 'top 90%',
                    once: true,
                    interval: 0.08,
                    batchMax: 6,
                    onEnter: (batch) => {
                        const tween = gsap.to(batch, {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            duration: 0.58,
                            stagger: 0.055,
                            ease: 'power2.out',
                            overwrite: true,
                            clearProps: 'transform,opacity,willChange'
                        });

                        this.productTweens.push(tween);
                    }
                });

                this.productTriggers.push(...batchTriggers);
                requestAnimationFrame(() => ScrollTrigger.refresh());
            }
        },
        beforeUnmount() {
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }

            this.animationQueued = false;
            this.clearProductAnimation();
        }
    }
</script>

<style scoped>
    .products-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 30px;
        align-items: stretch;
        box-sizing: border-box;
        width: min(100%, 1320px);
        margin: 0 auto;
        padding: 0 clamp(16px, 2vw, 32px);
    }

    .product-card {
        --product-image-surface: #f0efed;
        position: relative;
        isolation: isolate;
        display: flex;
        flex-direction: column;
        min-width: 0;
        color: inherit;
        text-decoration: none;
        background: #fff;
        border: 1px solid rgba(34, 34, 34, 0.08);
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 12px 30px rgba(24, 20, 18, 0.06);
        transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
    }

    .product-card:hover {
        transform: translateY(-6px);
        border-color: rgba(34, 34, 34, 0.14);
        box-shadow: 0 22px 50px rgba(24, 20, 18, 0.12);
    }

    .product-card:focus-visible {
        outline: 2px solid #222;
        outline-offset: 4px;
    }

    .product-image-wrap {
        position: relative;
        z-index: 1;
        aspect-ratio: 1 / 1.06;
        margin: 12px 12px 0;
        overflow: hidden;
        background: var(--product-image-surface);
        border-radius: 6px;
    }

    .product-image {
        width: 100%;
        height: 100%;
        background-size: min(72%, 280px);
        background-position: center;
        background-repeat: no-repeat;
        transition: transform 0.42s ease, filter 0.42s ease;
        transform-origin: center;
    }

    .product-card:hover .product-image {
        filter: saturate(1.04) contrast(1.02);
        transform: scale(1.05);
    }

    .product-info {
        position: relative;
        z-index: 1;
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: space-between;
        gap: 16px;
        min-height: 132px;
        padding: 18px 20px 20px;
        text-align: left;
    }

    .product-name {
        display: -webkit-box;
        min-height: 46px;
        margin: 0;
        overflow: hidden;
        color: #181818;
        font-family: MiSans-Regular, Helvetica, Arial, sans-serif;
        font-size: 16px;
        font-weight: normal;
        line-height: 1.45;
        text-overflow: ellipsis;
        transition: color 0.22s ease;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    .product-card:hover .product-name {
        color: #111;
    }

    .product-price {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 42px;
        margin: 0;
    }

    .price {
        color: #111;
        font-size: 18px;
        font-weight: 600;
        line-height: 1;
    }

    .add-to-cart {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        color: #222;
        background-color: transparent;
        border: 1px solid rgba(34, 34, 34, 0.12);
        border-radius: 8px;
        transition: background-color 0.24s ease, border-color 0.24s ease, color 0.24s ease, transform 0.24s ease, box-shadow 0.24s ease;
    }

    .product-card:hover .add-to-cart {
        color: white;
        background-color: #222;
        border-color: #222;
        box-shadow: 0 8px 18px rgba(24, 20, 18, 0.18);
        transform: translateY(-2px) scale(1.04);
    }

    @media (max-width: 1024px) {
        .products-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 22px;
        }
    }

    @media (max-width: 768px) {
        .products-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px;
            padding: 0 10px;
        }

        .product-image-wrap {
            aspect-ratio: 1 / 1.04;
            margin: 8px;
        }

        .product-image {
            background-size: min(74%, 160px);
        }

        .product-info {
            gap: 12px;
            padding: 0 12px 14px;
        }

        .product-name {
            min-height: 42px;
            font-size: 14px;
            line-height: 1.45;
        }

        .product-price {
            min-height: 32px;
        }

        .price {
            font-size: 16px;
        }

        .add-to-cart {
            width: 32px;
            height: 32px;
        }
    }

    @media (hover: none), (max-width: 768px) {
        .product-card:hover,
        .product-card:hover .product-image,
        .product-card:hover .add-to-cart {
            transform: none;
        }

    }

    @media (prefers-reduced-motion: reduce) {
        .product-card,
        .product-image,
        .add-to-cart,
        .product-image-wrap,
        .product-name {
            transition: none;
        }
    }

    @media (max-width: 420px) {
        .products-grid {
            gap: 10px;
        }

        .product-card {
            border-radius: 6px;
        }
    }
</style>

